import { TrackingInfo } from "@bridgesplit/ui";
import { TransactionResult } from "@bridgesplit/react";
import {
    DepositParams,
    EscrowTransferParams,
    IncreaseCreditTransactionParams,
    LockboxCollateralWithdraw,
    LockboxDepositParams,
    LoopUnwindParams,
    SellLoanTransactionParams,
    WithdrawParams
} from "@bridgesplit/abf-react";

import {
    SubmitEarnData,
    TrackEscrowWithdrawData,
    TrackMarketBorrowData,
    TrackMarketLendData,
    TrackSubmitRepayData
} from "./mixpanel-data";

export enum TrackEvent {
    PrimeApplicationStarted = "PrimeApplicationStarted",
    ViewYieldCurve = "ViewYieldCurve",
    ViewRateHistory = "ViewRateHistory",
    ViewLendPortfolio = "ViewLendPortfolio",
    ViewBorrowPortfolio = "ViewBorrowPortfolio",
    SubmitMarketGuideBorrow = "SubmitMarketGuideBorrow",
    SubmitMarketGuideLend = "SubmitMarketGuideLend",
    SubmitMarketGuideLoops = "SubmitMarketGuideLoops"
}

export enum TrackEventWithProps {
    SelectBorrowCollateral = "SelectBorrowCollateral",
    SelectBorrowPrincipal = "SelectBorrowPrincipal",
    OpenBorrowModal = "OpenBorrowModal",
    OpenLendModal = "OpenLendModal",
    ClaimCommunityReward = "ClaimCommunityReward",
    SelectBorrowDuration = "SelectBorrowDuration",
    SubmitDeleteStrategy = "SubmitDeleteStrategy"
}

export enum TrackTransactionEvent {
    SubmitMarketBorrowOrder = "SubmitMarketBorrowOrder",
    SubmitMarketLend = "SubmitMarketLend",
    SubmitRepay = "SubmitRepay",
    SubmitTopup = "SubmitTopup",
    RepayPrincipal = "RepayPrincipal",
    SubmitRefinance = "SubmitRefinance",
    EscrowDeposit = "EscrowDeposit",
    EscrowWithdraw = "EscrowWithdraw",
    EscrowTransfer = "EscrowTransfer",
    SubmitStrategyDeposit = "SubmitStrategyDeposit",
    SubmitStrategyWithdraw = "SubmitStrategyWithdraw",
    EditStrategy = "EditStrategy",
    CollateralWithdraw = "CollateralWithdraw",
    IncreasePrincipal = "IncreasePrincipal",
    SubmitSellLoan = "SubmitEarlyWithdraw",
    SubmitEarnOrder = "SubmitYieldLoopOrder",
    SubmitCloseEarnOrder = "SubmitCloseYieldLoopOrder"
}

export interface TransactionEventResponse {
    params?: object;
    res?: TransactionResult[];
    err?: string;
}

export type TrackTransactionEventType = {
    [TrackTransactionEvent.SubmitMarketBorrowOrder]: TrackMarketBorrowData;
    [TrackTransactionEvent.SubmitMarketLend]: TrackMarketLendData;
    [TrackTransactionEvent.SubmitRepay]: TrackSubmitRepayData;
    [TrackTransactionEvent.SubmitTopup]: LockboxDepositParams;
    [TrackTransactionEvent.RepayPrincipal]: TrackSubmitRepayData;
    [TrackTransactionEvent.SubmitRefinance]: TrackMarketBorrowData;
    [TrackTransactionEvent.EscrowDeposit]: DepositParams | undefined;
    [TrackTransactionEvent.EscrowWithdraw]: TrackEscrowWithdrawData | undefined;
    [TrackTransactionEvent.EscrowTransfer]: EscrowTransferParams;
    [TrackTransactionEvent.SubmitStrategyDeposit]: DepositParams;
    [TrackTransactionEvent.SubmitStrategyWithdraw]: WithdrawParams;
    [TrackTransactionEvent.CollateralWithdraw]: LockboxCollateralWithdraw;
    [TrackTransactionEvent.IncreasePrincipal]: IncreaseCreditTransactionParams;
    [TrackTransactionEvent.SubmitSellLoan]: SellLoanTransactionParams;
    [TrackTransactionEvent.SubmitEarnOrder]: SubmitEarnData;
    [TrackTransactionEvent.SubmitCloseEarnOrder]: LoopUnwindParams;
};
export type TrackTransactionData<T> = T extends keyof TrackTransactionEventType ? TrackTransactionEventType[T] : null;

type TokenSelect = { tokenSymbol: string };
type MarketPrincipal = { marketPrincipalMint: string };
type RewardClaim = { rewardName: string };
type Duration = { duration: string };
type Strategy = { strategyId: string };

type EventPropsMap = {
    [TrackEventWithProps.SelectBorrowCollateral]: TokenSelect;
    [TrackEventWithProps.SelectBorrowPrincipal]: TokenSelect;
    [TrackEventWithProps.OpenBorrowModal]: MarketPrincipal;
    [TrackEventWithProps.OpenLendModal]: MarketPrincipal;
    [TrackEventWithProps.ClaimCommunityReward]: RewardClaim;
    [TrackEventWithProps.SelectBorrowDuration]: Duration;
    [TrackEventWithProps.SubmitDeleteStrategy]: Strategy;
};

export class AppTracking implements TrackingInfo {
    public eventName: string;
    public properties?: object;

    private constructor(eventName: string, properties?: object) {
        this.eventName = eventName;
        this.properties = properties;
    }

    /**
     * Create a new TrackingInfo with strict typing for the event name and properties
     */
    static fromProps(eventName: TrackEventWithProps, properties: EventPropsMap[TrackEventWithProps]) {
        return new AppTracking(eventName, properties);
    }

    /**
     * Create a new TrackingInfo with strict typing for the TransactionEventResponse shape
     */
    static fromTransaction(eventName: TrackTransactionEvent, properties: TransactionEventResponse) {
        return new AppTracking(eventName, properties);
    }

    /**
     * Create a new TrackingInfo with just the event name and undefined properties
     */
    static from(eventName: TrackEvent) {
        return new AppTracking(eventName);
    }
}
